import axios from 'axios';
import toast from 'react-hot-toast';
import { performTokenRefresh } from './oauth/refreshFlow.ts';
import {
  applyModuleAccessDeniedMessage,
  isModuleAccessDeniedError,
  MODULE_ACCESS_DENIED_USER_MESSAGE,
} from '../request/moduleAccessDenied.ts';

/**
 * Show a toast for any API error that carries a user-facing detail message.
 * Used by both axiosInstance and aiAxiosInstance so every component gets it
 * for free without per-component error handling.
 */
function showApiErrorToast(error) {
  // 401s are handled separately by the auth refresh interceptor
  if (error?.response?.status === 401) return;
  // Go backend serialises errors as { message: "..." }; FastAPI uses { detail: "..." }.
  // Check both so toasts fire for all API layers.
  const msg =
    error?.response?.data?.detail ??
    error?.response?.data?.message;
  if (msg && typeof msg === 'string') {
    toast.error(msg, { duration: 6000, id: msg });
  }
}

// Backend API base URL. When frontend and backend are on different sites, this
// must point to the backend origin (e.g. https://your-backend.example.com or http://localhost:8080).
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8080')
const AUTH_BOOTSTRAP_FLAG = '__SKEPTICS_AUTH_BOOTSTRAP_IN_PROGRESS__';

/** Avoid toast spam when a page fires several module-gated APIs at once. */
let lastModuleAccessToastAt = 0;
const MODULE_ACCESS_TOAST_COOLDOWN_MS = 2500;

function toastModuleAccessDeniedOnce() {
  const now = Date.now();
  if (now - lastModuleAccessToastAt < MODULE_ACCESS_TOAST_COOLDOWN_MS) return;
  lastModuleAccessToastAt = now;
  toast.error(MODULE_ACCESS_DENIED_USER_MESSAGE);
}

console.log('API Base URL:', API_BASE_URL);

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// On 401: refresh token (same flow as admin helpers), then retry once. If refresh fails, user is signed out in refreshFlow.
// On 403 module access denied: normalize message + toast so every page surfaces the same copy.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isModuleAccessDeniedError(error)) {
      applyModuleAccessDeniedMessage(error);
      toastModuleAccessDeniedOnce();
      return Promise.reject(error);
    }

    const originalRequest = error?.config;
    if (!originalRequest || error?.response?.status !== 401) {
      return Promise.reject(error);
    }
    if (typeof window !== 'undefined' && window[AUTH_BOOTSTRAP_FLAG]) {
      return Promise.reject(error);
    }
    const url = originalRequest?.url || '';
    if (url.includes('/api/refresh') || url.includes('/api/signin') || url.includes('/api/auth/exchange')) {
      return Promise.reject(error);
    }
    if (originalRequest.__retriedAfterRefresh) {
      return Promise.reject(error);
    }

    try {
      await performTokenRefresh();
      originalRequest.__retriedAfterRefresh = true;
      return axiosInstance(originalRequest);
    } catch (e) {
      return Promise.reject(e);
    }
  }
);

// Global error toast for backend errors with a user-facing detail message
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    showApiErrorToast(error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const organizationId = window.localStorage.getItem('organization_id');
  const userId = window.localStorage.getItem('userId')
   if(process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_TOKEN_AUTH === 'true') {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
     }
  if (organizationId) config.headers['X-Organization-Id'] = organizationId;
  if (userId) config.headers['X-User-Id'] = userId;
  return config;
});

const AI_SERVICE_BASE_URL = process.env.REACT_APP_AI_SERVICE_URL || 'http://localhost:5001';

export const aiAxiosInstance = axios.create({
  baseURL: AI_SERVICE_BASE_URL,
  timeout: 120000,
});

aiAxiosInstance.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const organizationId = window.localStorage.getItem('organization_id');
  const userId = window.localStorage.getItem('userId');
  if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_TOKEN_AUTH === 'true') {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }
  if (organizationId) config.headers['X-Organization-Id'] = organizationId;
  if (userId) config.headers['X-User-Id'] = userId;
  return config;
});

// Global error toast for AI service errors with a user-facing detail message
aiAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    showApiErrorToast(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
